import { useHistory } from 'react-router-dom'
import classNames from 'classnames'
import { Alert } from '@cmsgov/ds-medicare-gov'
import { ClaimDetailsRequest } from '@cms-max/medicare-api-sdk'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'components/Button/Button'
import { MySpinner } from 'components/MySpinner/MySpinner'
import {
  getSearchedFilters,
  getSearchResults,
  getSearchResultsTotal,
  getStatus,
  showAllSearchResults,
  showMoreSearchResults,
  Status
} from 'redux/reducers/claims'
import { ClaimCard } from './ClaimCard'
import { ClaimsFilterDisplay } from './ClaimsFilterDisplay'

export interface ClaimResultsProps {
  className?: string
}

export const ClaimResults: FC<ClaimResultsProps> = ({ className }) => {
  const { t } = useTranslation('claimsSearch')
  const dispatch = useDispatch()
  const status = useSelector(getStatus)
  const claimResults = useSelector(getSearchResults)
  const searchedFilters = useSelector(getSearchedFilters)
  const totalClaimResults = useSelector(getSearchResultsTotal)
  const history = useHistory()

  const onShowDetails = (request: ClaimDetailsRequest) => {
    history.push('/claims/claim-details', request)
  }

  const isLoading = status === Status.LOADING
  const isError = status === Status.ERROR
  const isFinished = status === Status.FINISHED
  const hasClaimResults = isFinished && claimResults.length > 0
  const noClaimResults = isFinished && claimResults.length === 0
  const showLoadMoreShowMore = (claimResults.length < totalClaimResults) && !isError

  return (
    <section className={classNames('ds-l-container', className)}>
      <div className='ds-l-row'>
        <div className='ds-l-col--12'>
          <div className={classNames([
            'ds-u-border-top--1',
            'ds-u-display--flex',
            'ds-u-flex-direction--column',
            'ds-u-md-flex-direction--row',
            'ds-u-justify-content--between',
            'ds-u-padding-top--3'
          ])}>
            <div>
              <h2 className='ds-text-heading--2xl my-u-mb--16'>{t('currentSearchHeading')}</h2>
              {(hasClaimResults && !isError) && (
                <div aria-live='polite' aria-atomic='true'>
                  {t('paginationRange', {
                    start: 1,
                    end: claimResults.length,
                    total: totalClaimResults
                  })}
                </div>
              )}
              {(noClaimResults && !isError) && (
                <div aria-live='polite' aria-atomic='true'>
                  {t('noResultsPagination')}
                </div>
              )}
            </div>
            <ClaimsFilterDisplay filters={searchedFilters} />
          </div>
        </div>
      </div>
      <div className='ds-l-row'>
        <div className='ds-l-col--12'>
          {isLoading && <MySpinner />}
          {isError && (
            <Alert className='ds-u-margin-top--3' variation='warn' hideIcon>
              {t('searchResultsUnavailable')}
            </Alert>
          )}
          {(noClaimResults && !isError) && (
            <Alert className='ds-u-margin-top--3' variation='warn' hideIcon>
              {t('searchResultsEmpty')}
            </Alert>
          )}
          {(hasClaimResults && !isError) && claimResults.map(c => (
            <ClaimCard
              key={c.displayClaimNumber}
              className='ds-u-margin-top--3'
              claim={c}
              onShowDetails={onShowDetails} />
          ))}
        </div>
      </div>
      {showLoadMoreShowMore && isFinished && (
        <div className='ds-l-row ds-u-margin-top--3'>
          <div className='ds-l-col--12'>
            <Button
              className='ds-c-button ds-c-button--solid ds-c-button--alternate ds-u-margin-right--2 ds-u-margin-bottom--2'
              onClick={() => dispatch(showMoreSearchResults())}>
                {t('loadMoreClaimsButtonText')}
            </Button>
            <Button
              className='ds-c-button'
              onClick={() => dispatch(showAllSearchResults())}>
                {t('showAllClaimsButtonText')}
            </Button>
          </div>
        </div>
      )}
      <Alert hideIcon className='my-u-mt--8'>
        <h3 className='ds-text-heading--md ds-u-font-weight--bold ds-u-margin--0'>{t('claimInformationNote.noteHeading')}</h3>
        <ul>
          <li className='ds-u-font-size--sm ds-u-margin-top--0 ds-u-margin-bottom--1'>{t('claimInformationNote.line1')}</li>
          <li className='ds-u-font-size--sm ds-u-margin-bottom--1'>{t('claimInformationNote.line2')}</li>
          <li className='ds-u-font-size--sm'>{t('claimInformationNote.line3')}</li>
        </ul>
      </Alert>
    </section>
  )
}
